/**
 * isolateTailZone — T2 step 1 (ADR-031 §3.3, D1 ruled 2026-09-22): **turn a
 * tail Zone into the voxel grid its chain is placed in.**
 *
 * ONE responsibility: from the target's bind-world mesh and a zone's face set,
 * find where the tube meets the body, and voxelize the tube (plus a thin
 * collar of body) through the EXISTING `voxelizeMesh(cutPlane)`. It places no
 * joint and decides no count — `tip.ts` and `stations.ts` read the grid.
 *
 * THE ROOT IS THE BOUNDARY RING (D1). The zone's faces alone say where the
 * tail joins the body, before any grid exists: an edge used by a zone face and
 * a body face is a junction edge, and the largest connected loop of them is
 * the ring. Its centroid is the root; the cut plane is the ring's own
 * best-fit plane, its normal oriented away from the body (from the root toward
 * the zone's own centroid). The ruling said "normal = the zone's PCA long
 * axis"; on the tiger that axis is 87° from the ring's plane (see the cut,
 * below) — so the PCA axis is recorded as evidence and is only the fallback
 * for a ring with no plane. That breaks the chicken-and-egg the hand pipeline never had —
 * `voxelizeMesh` wants a cut plane before anything, and the root is "where
 * the tube meets the body" — without widening the voxelizer for one caller.
 *
 * WELDED, NOT INDEXED. Generated meshes split vertices at every UV seam, so
 * the tail's ring vertex and the buttock's are often two indices at one
 * position. Adjacency is therefore read over POSITION-WELDED vertices; a
 * ring measured by index would be empty on exactly the specimens that matter.
 *
 * THE CAP IS WHAT MAKES IT SOLID. The voxelizer fills interior by flooding
 * EMPTY inward from the grid's border — an open tube (and a zone is open where
 * it meets the body) floods hollow, and a hollow tail puts every medial-path
 * centroid in the cavity. So every boundary loop of the zone is capped with a
 * fan to its own centroid before voxelizing; the ring's cap is the cut face.
 * Other loops (a hole in a hand-edited zone, a detached scrap's rim) are
 * capped too and REPORTED — never silent.
 *
 * THE COLLAR (D1). One ring of body faces around the junction joins the grid
 * so the root has flesh on the body side: the re-seat's retained band is
 * measured against it, and the geodesic field has somewhere to start that is
 * not the tail's own surface.
 *
 * Refusals are product outcomes a person can act on, and they name the zone:
 * a zone whose faces touch no body face has no root (a detached shell, or the
 * whole mesh pinned), and a face index outside the mesh means the zone was cut
 * on another mesh (the fingerprint door should have said so first).
 *
 * Kernel-pure: typed arrays in, a grid out. Depends on: hands/voxelize,
 * hands/voxel-grid, hands/voxel-math, hands/separation-profile (the marker
 * shape). Depended on by: tails/place-tail.
 */
import { KernelError } from '../ir/errors.js';
import type { Vec3Data } from '../math/vec3.js';
import { voxelizeMesh, type CutPlane } from '../hands/voxelize.js';
import { worldPointToVoxel, type VoxelGrid, type VoxelPoint } from '../hands/voxel-grid.js';
import { centroidOf, dominantAxis, dot, sub } from '../hands/voxel-math.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';

/**
 * A tail that cannot be placed on THIS zone — a product outcome, named for the
 * person (ADR-031 §8), never a crash. The message is what the flyout shows.
 */
export class TailRefusal extends KernelError {
  constructor(message: string) {
    super(message);
    this.name = 'TailRefusal';
  }
}

/**
 * The FAN refusal, typed: more than one tail leaves this root. Carries the count
 * so the flyout can offer "Group as one tail" when the fan is only found at Grow
 * — a wand-made zone has no detector reading to predict it (Penny, 2026-09-26).
 */
export class TailFanRefusal extends TailRefusal {
  constructor(
    message: string,
    readonly tails: number,
  ) {
    super(message);
    this.name = 'TailFanRefusal';
  }
}

export interface TailZoneMesh {
  /** Bind-world positions, xyz interleaved (`position · bindMatrix`, the pipeline's one definition). */
  readonly positions: Float32Array | Float64Array | readonly number[];
  /** Triangle list, three indices per face. */
  readonly indices: Uint32Array | Uint16Array | readonly number[];
}

export interface IsolateTailZoneOptions {
  /** The zone's name — every refusal speaks it. */
  readonly zoneName: string;
  /** Voxels along the longest axis of zone + collar (default {@link TAIL_RESOLUTION_DEFAULT}). */
  readonly resolution?: number;
  /** A person's root (bind-world) — replaces the ring centroid when given. */
  readonly root?: Vec3Data;
}

/** Default voxel resolution: the hands' own default, spent on the tail rather than the body. */
export const TAIL_RESOLUTION_DEFAULT = 64;

export interface IsolatedTailZone {
  /** The tail's grid — `origin` set, `wristSeed` = the root band on the tail side of the cut. */
  readonly grid: VoxelGrid;
  /** Where the chain starts, bind-world: the ring centroid (or the person's root). */
  readonly root: Vec3Data;
  /** The root in voxel-index space. */
  readonly rootVoxel: VoxelPoint;
  /** The cut plane: point = root, normal = the ring's plane normal pointing AWAY from the body. */
  readonly cutPlane: CutPlane;
  /** Every vertex index a zone face uses — the flesh the tail will own (D3). */
  readonly zoneVertices: Uint32Array;
  /** Vertex indices on the junction ring (all position-welded twins included). */
  readonly ringVertices: Uint32Array;
  /** Mean distance from the ring's vertices to the root — the tail's radius where it meets the body. */
  readonly ringRadius: number;
  /** Body faces sharing a vertex with the ring — the collar voxelized beside the zone. */
  readonly collarFaces: Uint32Array;
  /** Angle (degrees) between the ring's best-fit plane normal and the zone's PCA long axis — evidence (87° on the tiger). */
  readonly ringPlaneAngleDeg: number;
  readonly markers: readonly EvidenceMarker[];
}

const vec = (p: ArrayLike<number>, i: number): VoxelPoint => ({ x: p[i * 3]!, y: p[i * 3 + 1]!, z: p[i * 3 + 2]! });

/** Smallest-variance axis of a point set — the normal of its best-fit plane (evidence only). */
function planeNormal(points: readonly VoxelPoint[]): VoxelPoint {
  const mean = centroidOf(points);
  const c = [0, 0, 0, 0, 0, 0]; // xx yy zz xy xz yz
  for (const p of points) {
    const d = sub(p, mean);
    c[0]! += d.x * d.x; c[1]! += d.y * d.y; c[2]! += d.z * d.z;
    c[3]! += d.x * d.y; c[4]! += d.x * d.z; c[5]! += d.y * d.z;
  }
  // Inverse power iteration via the adjugate is overkill here: take the cross
  // product of the two dominant directions (power iteration + deflation).
  const mul = (v: VoxelPoint): VoxelPoint => ({
    x: c[0]! * v.x + c[3]! * v.y + c[4]! * v.z,
    y: c[3]! * v.x + c[1]! * v.y + c[5]! * v.z,
    z: c[4]! * v.x + c[5]! * v.y + c[2]! * v.z,
  });
  const norm = (v: VoxelPoint): VoxelPoint => {
    const l = Math.hypot(v.x, v.y, v.z);
    return l > 1e-12 ? { x: v.x / l, y: v.y / l, z: v.z / l } : { x: 0, y: 0, z: 0 };
  };
  let a: VoxelPoint = norm({ x: 1, y: 0.37, z: 0.13 });
  for (let i = 0; i < 100; i++) a = norm(mul(a));
  let b: VoxelPoint = norm({ x: 0.21, y: 1, z: 0.43 });
  for (let i = 0; i < 100; i++) {
    const w = mul(b);
    const k = dot(w, a);
    b = norm({ x: w.x - k * a.x, y: w.y - k * a.y, z: w.z - k * a.z });
  }
  return norm({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
}

/**
 * Isolate a tail zone into a voxel grid. See the module header; throws
 * {@link TailRefusal} for a zone the person must change, and the voxelizer's
 * own `InvalidMeshError` for a structurally broken mesh.
 */
export function isolateTailZone(mesh: TailZoneMesh, faces: readonly number[], options: IsolateTailZoneOptions): IsolatedTailZone {
  const { positions, indices } = mesh;
  const name = options.zoneName;
  const vertexCount = Math.floor(positions.length / 3);
  const faceCount = Math.floor(indices.length / 3);
  if (faces.length === 0) throw new TailRefusal(`zone "${name}" has no faces — nothing to place a tail in.`);
  const zoneFace = new Uint8Array(faceCount);
  for (const f of faces) {
    if (!Number.isInteger(f) || f < 0 || f >= faceCount) {
      throw new TailRefusal(
        `zone "${name}" names face ${f}, but this mesh has ${faceCount} faces — the zone was cut on another mesh; re-pin it on this one.`,
      );
    }
    zoneFace[f] = 1;
  }

  // --- weld by exact position: seams split vertices, adjacency must not ---------
  const weld = new Int32Array(vertexCount);
  const firstAt = new Map<string, number>();
  for (let v = 0; v < vertexCount; v++) {
    const key = `${positions[v * 3]},${positions[v * 3 + 1]},${positions[v * 3 + 2]}`;
    const seen = firstAt.get(key);
    if (seen === undefined) {
      firstAt.set(key, v);
      weld[v] = v;
    } else {
      weld[v] = seen;
    }
  }

  // --- edges over welded vertices: how many zone faces and body faces use each --
  const edgeKey = (a: number, b: number): number => (a < b ? a * vertexCount + b : b * vertexCount + a);
  const zoneUse = new Map<number, number>();
  const bodyUse = new Map<number, number>();
  for (let f = 0; f < faceCount; f++) {
    const w0 = weld[indices[f * 3]!]!;
    const w1 = weld[indices[f * 3 + 1]!]!;
    const w2 = weld[indices[f * 3 + 2]!]!;
    const use = zoneFace[f] === 1 ? zoneUse : bodyUse;
    for (const [a, b] of [[w0, w1], [w1, w2], [w2, w0]] as const) {
      if (a === b) continue; // a degenerate sliver edge
      const k = edgeKey(a, b);
      use.set(k, (use.get(k) ?? 0) + 1);
    }
  }
  // A boundary edge of the zone surface: one zone face uses it. It is a
  // JUNCTION edge when a body face uses it too; otherwise it is the mesh's own
  // open edge (or a non-manifold seam) inside the zone.
  const boundary: { a: number; b: number; junction: boolean }[] = [];
  for (const [k, n] of zoneUse) {
    if (n !== 1) continue;
    const a = Math.floor(k / vertexCount);
    const b = k % vertexCount;
    boundary.push({ a, b, junction: (bodyUse.get(k) ?? 0) > 0 });
  }

  // --- boundary loops: connected components of boundary edges ----------------
  const parent = new Map<number, number>();
  const find = (x: number): number => {
    let r = x;
    while (parent.get(r)! !== r) r = parent.get(r)!;
    let c = x;
    while (parent.get(c)! !== r) {
      const n = parent.get(c)!;
      parent.set(c, r);
      c = n;
    }
    return r;
  };
  for (const e of boundary) {
    if (!parent.has(e.a)) parent.set(e.a, e.a);
    if (!parent.has(e.b)) parent.set(e.b, e.b);
    const ra = find(e.a);
    const rb = find(e.b);
    if (ra !== rb) parent.set(ra, rb);
  }
  const loops = new Map<number, { edges: { a: number; b: number }[]; junctionEdges: number; verts: Set<number> }>();
  for (const e of boundary) {
    const r = find(e.a);
    const loop = loops.get(r) ?? { edges: [], junctionEdges: 0, verts: new Set<number>() };
    loop.edges.push(e);
    if (e.junction) loop.junctionEdges++;
    loop.verts.add(e.a);
    loop.verts.add(e.b);
    loops.set(r, loop);
  }
  const loopList = [...loops.values()].sort((x, y) => y.junctionEdges - x.junctionEdges || y.edges.length - x.edges.length);
  const ring = loopList[0];
  if (ring === undefined || ring.junctionEdges === 0) {
    throw new TailRefusal(
      `zone "${name}" touches no body face — a tail's root is where it meets the body, and this zone ` +
        `${ring === undefined ? 'is a closed shell' : 'only has open edges'}. Grow the zone toward the body (wider), or pin the flesh that joins it.`,
    );
  }
  const markers: EvidenceMarker[] = [];
  if (loopList.length > 1) {
    markers.push({
      code: 'tail-zone-extra-loops-capped',
      detail: {
        zone: name,
        loops: loopList.length - 1,
        edges: loopList.slice(1).reduce((n, l) => n + l.edges.length, 0),
      },
    });
  }

  // --- root, axis, cut plane ----------------------------------------------------
  const ringWelded = [...ring.verts];
  const ringPoints = ringWelded.map((w) => vec(positions, w));
  const ringCentroid = centroidOf(ringPoints);
  const zoneVertexSet = new Set<number>();
  for (const f of faces) for (let k = 0; k < 3; k++) zoneVertexSet.add(indices[f * 3 + k]!);
  const zoneWelded = new Set<number>();
  for (const v of zoneVertexSet) zoneWelded.add(weld[v]!);
  const zonePoints = [...zoneWelded].map((w) => vec(positions, w));
  const zoneCentroid = centroidOf(zonePoints);
  const root: VoxelPoint = options.root !== undefined ? options.root : ringCentroid;
  const away = (v: VoxelPoint): VoxelPoint => (dot(sub(zoneCentroid, root), v) < 0 ? { x: -v.x, y: -v.y, z: -v.z } : v);
  // THE CUT IS THE RING'S OWN PLANE (measured 2026-09-22, the tiger). D1 as
  // ruled reads "best-fit plane, normal = the zone's PCA long axis" — two
  // statements that agree on a straight tail and DISAGREE on a real one: the
  // tiger's tail leaves the body backward and hangs down, so its PCA axis is
  // vertical while the ring's best-fit plane faces back — 87° apart. A
  // horizontal cut through that root left the upper half of the tail's base on
  // the thigh's side of the plane. The ring's plane is the one that says
  // "where the tube meets the body"; the PCA axis is kept as evidence (and as
  // the fallback for a ring too degenerate to have a plane — a straight seam).
  const pcaAxis = away(dominantAxis(zonePoints));
  const ringNormal = planeNormal(ringPoints);
  const ringUsable = Math.hypot(ringNormal.x, ringNormal.y, ringNormal.z) > 0.5 && ringPoints.length >= 3;
  const axis = ringUsable ? away(ringNormal) : pcaAxis;
  const ringPlaneAngleDeg = (Math.acos(Math.min(1, Math.abs(dot(ringNormal, pcaAxis)))) * 180) / Math.PI;
  markers.push({
    code: ringUsable ? 'tail-cut-from-ring-plane' : 'tail-cut-from-zone-axis',
    detail: { zone: name, ringVsAxisDeg: Number(ringPlaneAngleDeg.toFixed(1)) },
  });
  const ringRadius = ringPoints.reduce((s, p) => s + Math.hypot(p.x - ringCentroid.x, p.y - ringCentroid.y, p.z - ringCentroid.z), 0) / ringPoints.length;

  // --- the collar: body faces touching the ring (by welded vertex) --------------
  const ringSet = ring.verts;
  const collar: number[] = [];
  for (let f = 0; f < faceCount; f++) {
    if (zoneFace[f] === 1) continue;
    for (let k = 0; k < 3; k++) {
      if (ringSet.has(weld[indices[f * 3 + k]!]!)) {
        collar.push(f);
        break;
      }
    }
  }

  // --- the sub-mesh: zone + collar + a cap fan per boundary loop -----------------
  const subPositions: number[] = [];
  const subIndices: number[] = [];
  const local = new Map<number, number>();
  const at = (v: number): number => {
    const w = weld[v]!;
    let i = local.get(w);
    if (i === undefined) {
      i = subPositions.length / 3;
      local.set(w, i);
      subPositions.push(positions[w * 3]!, positions[w * 3 + 1]!, positions[w * 3 + 2]!);
    }
    return i;
  };
  for (const f of [...faces, ...collar]) {
    subIndices.push(at(indices[f * 3]!), at(indices[f * 3 + 1]!), at(indices[f * 3 + 2]!));
  }
  for (const loop of loopList) {
    const c = centroidOf([...loop.verts].map((w) => vec(positions, w)));
    const centre = subPositions.length / 3;
    subPositions.push(c.x, c.y, c.z);
    for (const e of loop.edges) subIndices.push(at(e.a), at(e.b), centre);
  }

  const cutPlane: CutPlane = { point: [root.x, root.y, root.z], normal: [axis.x, axis.y, axis.z] };
  const resolution = options.resolution ?? TAIL_RESOLUTION_DEFAULT;
  const grid = voxelizeMesh(subPositions, subIndices, { cutPlane, resolution });

  // Twins included: the tail owns every index of its flesh, not one per position.
  const ringVertices: number[] = [];
  for (let v = 0; v < vertexCount; v++) if (ringSet.has(weld[v]!)) ringVertices.push(v);

  return {
    grid,
    root: { x: root.x, y: root.y, z: root.z },
    rootVoxel: worldPointToVoxel(grid, root),
    cutPlane,
    zoneVertices: Uint32Array.from([...zoneVertexSet].sort((a, b) => a - b)),
    ringVertices: Uint32Array.from(ringVertices),
    ringRadius,
    collarFaces: Uint32Array.from(collar),
    ringPlaneAngleDeg,
    markers,
  };
}
